<template>
  <div class="page road-exclusions">
    <div class="title_line flex items-center justify-between">
      <div class="title">
        <h1 class="page-title">Road Exclusions</h1>
      </div>
      <div class="actions flex items-center gap-10">
        <template v-if="checkedItems.length > 1">
          <button class="icon-btn border-green" type="button" @click="askBulkStatus(true)" title="Activate">
            <img src="@/assets/images/circle-check.svg" alt="activate" />
          </button>
          <button class="icon-btn border-blue" type="button" @click="askBulkStatus(false)" title="Deactivate">
            <img src="@/assets/images/ban.svg" alt="deactivate" />
          </button>
          <button class="icon-btn border-red" type="button" @click="askBulkDelete" title="Delete">
            <img src="@/assets/images/delete.svg" alt="delete" />
          </button>
        </template>
        <router-link class="add-btn flex items-center" :to="{ name: 'NewAccountRoadExclusion' }">
          <img class="plus-icon" src="@/assets/images/plus.svg" alt="" />
          <span>Add Road Exclusion</span>
        </router-link>
      </div>
    </div>

    <div v-if="!loading && items_count === 0 && !search && !appliedSearch" class="empty-state flex flex-col items-center justify-center">
      <img class="empty-image" src="@/assets/images/road-exclusions-image-placeholder.png" alt="" />
      <p class="empty-text">There are no saved road exclusions</p>
    </div>

    <template v-else>
      <div class="search-block flex items-center">
        <select v-model="searchColumn" class="search-column" @change="onSearchColumnChange">
          <option value="name">Name</option>
          <option value="segment_id">Segment ID</option>
        </select>
        <span class="search-divider"></span>
        <div class="search-input-wrap flex items-center w-full">
          <input
            v-model="search"
            type="text"
            class="search-input"
            placeholder="Search..."
            @input="onSearchInput"
            @keyup.enter="reloadNow"
          />
          <img class="search-icon" src="@/assets/images/search.svg" alt="" @click="reloadNow" />
        </div>
      </div>

      <div class="filters-row flex items-center justify-between px-4 mt-4">
        <div class="rows-select flex items-center gap-5">
          <span>Rows:</span>
          <select v-model.number="take" @change="onTakeChange">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </div>
        <div class="pager flex items-center gap-10">
          <button type="button" :disabled="skip <= 0" @click="prevPage">Prev</button>
          <span>{{ rangeLabel }}</span>
          <button type="button" :disabled="skip + take >= items_count" @click="nextPage">Next</button>
        </div>
      </div>

      <div class="fl_table mt-4 px-4">
        <div class="wrap">
          <div class="header py-3 px-2 flex text-left">
            <div class="col col-check">
              <input type="checkbox" v-model="isCheckAll" @change="toggleCheckAll" />
            </div>
            <div class="col col-name sortable" @click="sortBy('name')">
              <span>Name</span>
              <span class="sort" :class="{ rotate: ascending && sort_by === 'name', hidden: sort_by !== 'name' }">
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.82406 6.80139C5.42671 7.37935 4.57332 7.37936 4.17597 6.80139L0.576992 1.56653C0.120833 0.903028 0.595854 8.78263e-08 1.40103 1.58217e-07L8.59897 -1.66193e-07C9.40415 -9.58021e-08 9.87917 0.903026 9.42302 1.56653L5.82406 6.80139Z" fill="black"/>
                </svg>
              </span>
            </div>
            <div class="col col-status">Status</div>
            <div class="col col-seg">Segment ID</div>
            <div class="col col-from">From</div>
            <div class="col col-to">To</div>
            <div class="col col-added">Added</div>
            <div class="col col-actions"></div>
          </div>
          <div class="body py-2">
            <div
              v-for="item in items"
              :key="item.id"
              class="row py-5 px-2 flex text-left"
              :class="{ active: checkedItems.indexOf(item.id) !== -1 }"
            >
              <div class="col col-check">
                <input type="checkbox" :value="item.id" v-model="checkedItems" />
              </div>
              <div class="col col-name name-link" @click="goView(item.id)">{{ item.name }}</div>
              <div
                class="col col-status"
                :class="item.is_active ? 'status-active' : 'status-inactive'"
                @click="goView(item.id)"
              >
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </div>
              <div class="col col-seg" @click="goView(item.id)">{{ item.segment_id }}</div>
              <div class="col col-from" @click="goView(item.id)">{{ formatDate(item.valid_from) }}</div>
              <div class="col col-to" @click="goView(item.id)">{{ formatDate(item.valid_to) }}</div>
              <div class="col col-added" @click="goView(item.id)">{{ formatDate(item.created_at) }}</div>
              <div class="col col-actions flex items-center gap-5" v-if="checkedItems.length <= 1">
                <button type="button" class="row-icon" @click="goEdit(item.id)">
                  <img src="@/assets/images/edit.svg" alt="edit" />
                </button>
                <button type="button" class="row-icon" @click="askToggle(item)">
                  <img v-if="item.is_active" src="@/assets/images/ban.svg" alt="deactivate" />
                  <img v-else src="@/assets/images/circle-check.svg" alt="activate" />
                </button>
                <button type="button" class="row-icon" @click="askDelete(item)">
                  <img src="@/assets/images/delete.svg" alt="delete" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <ConfirmActionModal
      :show="confirm.show"
      :title="confirm.title"
      @cancel="confirm.show = false"
      @confirm="runConfirm"
    />
  </div>
</template>

<script>
import ConfirmActionModal from '@/components/ConfirmActionModal.vue';
import { infoMessage } from '@/services/messages';
import moment from 'moment';

export default {
  name: 'RoadExclusions',
  components: { ConfirmActionModal },
  data() {
    return {
      loading: false,
      items: [],
      items_count: 0,
      skip: 0,
      take: 10,
      search: '',
      appliedSearch: '',
      searchTimer: null,
      searchColumn: 'name',
      sort_by: 'name',
      ascending: true,
      checkedItems: [],
      isCheckAll: false,
      confirm: {
        show: false,
        title: '',
        action: null,
      },
    };
  },
  computed: {
    rangeLabel() {
      if (!this.items_count) return '0-0 of 0';
      const from = this.skip + 1;
      const to = Math.min(this.skip + this.take, this.items_count);
      return from + '-' + to + ' of ' + this.items_count;
    },
  },
  watch: {
    checkedItems() {
      this.isCheckAll =
        this.items.length > 0 &&
        this.checkedItems.length === this.items.length;
    },
  },
  async mounted() {
    await this.reload();
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  },
  methods: {
    apiBase() {
      return this.$http.apiUrl() + '/road-exclusions';
    },
    formatDate(val) {
      if (!val) return '—';
      return moment(val).format('DD/MM/YYYY');
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.skip = 0;
        this.reload();
      }, 500);
    },
    reloadNow() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.skip = 0;
      this.reload();
    },
    onSearchColumnChange() {
      this.skip = 0;
      this.reload();
    },
    async reload() {
      this.appliedSearch = this.search || '';
      this.loading = true;
      try {
        const url =
          this.apiBase() +
          '?skip=' +
          this.skip +
          '&take=' +
          this.take +
          '&sort-by=' +
          this.sort_by +
          '&sort=' +
          this.ascending +
          '&search=' +
          encodeURIComponent(this.search || '') +
          '&column=' +
          this.searchColumn;
        const response = await this.$http.getAuth(url);
        this.items = (response.data && response.data.items) || [];
        this.items_count = (response.data && response.data.count) || 0;
        this.checkedItems = [];
      } catch (e) {
        infoMessage('Failed to load road exclusions', 'danger');
        this.items = [];
        this.items_count = 0;
      } finally {
        this.loading = false;
      }
    },
    sortBy(column) {
      if (this.sort_by === column) {
        this.ascending = !this.ascending;
      } else {
        this.sort_by = column;
        this.ascending = true;
      }
      this.reload();
    },
    onTakeChange() {
      this.skip = 0;
      this.reload();
    },
    prevPage() {
      this.skip = Math.max(0, this.skip - this.take);
      this.reload();
    },
    nextPage() {
      this.skip = this.skip + this.take;
      this.reload();
    },
    toggleCheckAll() {
      if (this.isCheckAll) {
        this.checkedItems = this.items.map((i) => i.id);
      } else {
        this.checkedItems = [];
      }
    },
    goView(id) {
      this.$router.push({ name: 'ViewAccountRoadExclusion', params: { id: String(id) } });
    },
    goEdit(id) {
      this.$router.push({ name: 'EditAccountRoadExclusion', params: { id: String(id) } });
    },
    askToggle(item) {
      const nextActive = !item.is_active;
      this.confirm = {
        show: true,
        title: item.is_active
          ? 'Set this road exclusion to Inactive?'
          : 'Set this road exclusion to Active?',
        action: async () => {
          await this.patchStatus(item.id, nextActive);
          await this.reload();
        },
      };
    },
    async patchStatus(id, is_active) {
      const axios = require('axios');
      await axios.patch(
        this.apiBase() + '/' + id + '/status',
        { is_active },
        { headers: this.$http.getAuthHeader() },
      );
    },
    askDelete(item) {
      this.confirm = {
        show: true,
        title: 'Delete this road exclusion?',
        action: async () => {
          const axios = require('axios');
          await axios.delete(this.apiBase() + '/' + item.id, {
            headers: this.$http.getAuthHeader(),
          });
          await this.reload();
        },
      };
    },
    askBulkStatus(is_active) {
      this.confirm = {
        show: true,
        title: is_active
          ? 'Activate selected road exclusions?'
          : 'Deactivate selected road exclusions?',
        action: async () => {
          await this.$http.postAuth(this.apiBase() + '/bulk/status', {
            ids: this.checkedItems,
            is_active,
          });
          await this.reload();
        },
      };
    },
    askBulkDelete() {
      this.confirm = {
        show: true,
        title: 'Delete selected road exclusions?',
        action: async () => {
          await this.$http.postAuth(this.apiBase() + '/bulk/delete', {
            ids: this.checkedItems,
          });
          await this.reload();
        },
      };
    },
    async runConfirm() {
      try {
        if (this.confirm.action) {
          await this.confirm.action();
        }
      } catch (e) {
        infoMessage('Action failed', 'danger');
      } finally {
        this.confirm.show = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-title {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  color: #000000;
  margin: 0;
}
.add-btn {
  height: 44px;
  border-radius: 8px;
  gap: 5px;
  padding: 10px 15px;
  background: #1FBC2F;
  color: #FFFFFF;
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  text-decoration: none;
  .plus-icon {
    width: 14px;
    height: 14px;
  }
}
.icon-btn {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &.border-green { border: 2px solid #1FBC2F; }
  &.border-blue { border: 2px solid #2468CA; }
  &.border-red { border: 2px solid #F2374D; }
  img { width: 20px; height: 20px; }
}
.empty-state {
  min-height: 60vh;
  .empty-image { max-width: 280px; }
  .empty-text {
    margin-top: 20px;
    font-family: Catamaran, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    color: #969A9F;
  }
}
.search-block {
  margin: 20px auto 0;
  width: 95%;
  background: #FFFFFF;
  box-shadow: 0px 1px 8px 0px #0C58B014;
  border-radius: 8px;
  padding: 8px 12px;
  .search-column {
    border: none;
    outline: none;
    font-family: Catamaran, sans-serif;
    font-size: 14px;
    color: #303741;
    background: transparent;
  }
  .search-divider {
    width: 1px;
    height: 24px;
    background: #E2E4E6;
    margin: 0 12px;
  }
  .search-input {
    border: none;
    outline: none;
    width: 100%;
    font-family: Catamaran, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: #303741;
    &::placeholder { color: #969A9F; }
  }
  .search-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}
.fl_table {
  .header {
    background: #F5F7F8;
    font-family: Catamaran, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    .col-name { color: #000; }
    .col-status, .col-seg, .col-from, .col-to, .col-added { color: #969A9F; }
  }
  .row {
    background: #FFFFFF;
    border-bottom: 1px solid #E2E4E6;
    &.active { background: #FFF1CE; }
  }
  .col { padding: 0 6px; display: flex; align-items: center; }
  .col-check { width: 40px; }
  .col-name { width: 22%; }
  .col-status { width: 12%; }
  .col-seg { width: 16%; }
  .col-from, .col-to, .col-added { width: 12%; }
  .col-actions { width: 12%; justify-content: flex-end; }
  .name-link {
    cursor: pointer;
    font-family: Catamaran, sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 14px;
    color: #1FBC2F;
  }
  .status-active {
    cursor: pointer;
    font-weight: 600;
    color: #0F9E1D;
  }
  .status-inactive {
    cursor: pointer;
    font-weight: 600;
    color: #969A9F;
  }
  .col-seg, .col-from, .col-to, .col-added {
    cursor: pointer;
    font-family: Catamaran, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: #303741;
  }
  .sortable { cursor: pointer; gap: 8px; }
  .sort.hidden { visibility: hidden; }
  .sort.rotate { transform: rotate(180deg); }
  .row-icon {
    background: transparent;
    border: none;
    padding: 4px;
    cursor: pointer;
    img { width: 20px; height: 20px; }
  }
}
.gap-5 { gap: 5px; }
.gap-10 { gap: 10px; }
</style>
